import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowLeft, Eye, Send, ShieldAlert } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { FormEvent, useState } from "react";

import { ApiError, CurrentSession, api } from "../api";
import { MutationMessage } from "../account/components";
import { HumanChallenge } from "../HumanChallenge";
import { ThemeSwitcher } from "../theme";

export function AppealScreen() {
  const reduceMotion = useReducedMotion();
  const query = new URLSearchParams(window.location.search);
  const fromAccount = query.get("from") === "account";
  const session = useQuery({
    queryKey: ["session"],
    queryFn: async () => {
      try {
        return await api<CurrentSession>("/api/v1/session");
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    enabled: fromAccount,
    retry: false,
    staleTime: 15_000,
  });
  const boundSession = fromAccount ? session.data : null;
  const [identifier, setIdentifier] = useState(query.get("identifier")?.slice(0, 254) ?? "");
  const [category, setCategory] = useState("ACCOUNT_LOCKED");
  const [message, setMessage] = useState("");
  const [challenge, setChallenge] = useState<{ ready: boolean; token: string | null }>({ ready: false, token: null });
  const [challengeAttempt, setChallengeAttempt] = useState(0);
  const submit = useMutation({
    mutationFn: () => api<{ status: string }>("/api/v1/account-appeals", {
      method: "POST",
      csrf: false,
      body: JSON.stringify({ identifier: boundSession ? null : identifier, category, message, challengeToken: challenge.token }),
    }),
    onError: () => setChallengeAttempt((value) => value + 1),
  });

  function send(event: FormEvent) {
    event.preventDefault();
    submit.mutate();
  }

  return (
    <main className="auth-shell">
      <section className="identity-pane" aria-labelledby="appeal-title">
        <div className="auth-brand-row"><a className="identity-mark" href="/"><Eye aria-hidden="true" /><span>EvilEyeAuth</span></a><ThemeSwitcher /></div>
        <motion.div className="auth-content" initial={reduceMotion ? false : { opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.24 }}>
          <a className="text-button back-button" href={fromAccount ? "/account" : "/"}><ArrowLeft aria-hidden="true" />{fromAccount ? "返回账号中心" : "返回登录"}</a>
          {submit.isSuccess ? (
            <div className="result-state"><ShieldAlert aria-hidden="true" /><h1 id="appeal-title">申诉已接收</h1><p>如果账号存在且符合处理条件，运营团队会通过账号已验证的联系渠道跟进。</p></div>
          ) : (
            <>
              <div className="heading-block compact"><span className="eyebrow">ACCOUNT SUPPORT</span><h1 id="appeal-title">提交账号申诉</h1><p>用于账号锁定、暂停、恢复受阻或安全事件。</p></div>
              <form className="auth-form" onSubmit={send}>
                {boundSession ? (
                  <label><span>申诉账号</span><input readOnly value={`@${boundSession.username}`} /></label>
                ) : (
                  <label><span>用户名或邮箱</span><input required minLength={3} maxLength={254} autoComplete="username" value={identifier} onChange={(event) => setIdentifier(event.target.value)} /></label>
                )}
                <label><span>问题类型</span><select value={category} onChange={(event) => setCategory(event.target.value)}><option value="ACCOUNT_LOCKED">账号被锁定</option><option value="SUSPENSION_APPEAL">暂停申诉</option><option value="RECOVERY_HELP">恢复受阻</option><option value="SECURITY_INCIDENT">安全事件</option></select></label>
                <label><span>情况说明</span><textarea required minLength={20} maxLength={4000} rows={7} value={message} onChange={(event) => setMessage(event.target.value)} /></label>
                <HumanChallenge action="appeal" onChange={setChallenge} resetKey={challengeAttempt} />
                <button className="primary-button wide" disabled={submit.isPending || !challenge.ready} type="submit"><Send aria-hidden="true" />提交申诉</button>
                <MutationMessage error={submit.error} />
              </form>
            </>
          )}
        </motion.div>
        <footer className="auth-footer"><span>反枚举响应</span><span>加密存储</span><span>人工审核</span></footer>
      </section>
      <aside className="signal-pane appeal-signal" aria-hidden="true"><div className="signal-grid" /><div className="signal-status"><span className="status-dot" />SUPPORT CHANNEL / PROTECTED</div><div className="signal-copy"><span>RECOVERY / REVIEW / TRUST</span><strong>每次处置都有证据。</strong><p>申诉内容只向具备支持职责的运营人员开放。</p></div></aside>
    </main>
  );
}
